import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { Lightbox } from './Lightbox.jsx';

// The manager's handoff queue (D247, CJ 2026-08-08).
//
// Its own list, not mixed into the injury queue — that was the whole point of
// D247. Two actions, in the order the ask describes:
//
//   Acknowledge — visible to the reporter within seconds. Cheap, and it is
//     what stops someone concluding that reporting is pointless.
//   Follow up   — one line the reporter sees about what was done. Silence is
//     what breeds cynicism; this is the closing of the loop.
//
// The card leads with CONTEXT, not the accusation: how many were on, how
// scheduled compares to who actually clocked in, and how busy it was. A solo
// close on a night 38% above normal and a two-person close on a quiet one are
// different conversations, and the manager should know which one they are
// walking into before they open their mouth.
export function ShiftHandoffQueue({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [rows, setRows] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [notes, setNotes] = useState({});
  const [err, setErr] = useState('');
  const [lightboxSrc, setLightboxSrc] = useState(null);

  async function load() {
    setErr('');
    try {
      const r = await api('GET', `/api/admin/shift-handoff?status=${showAll ? 'all' : 'open'}`, null, headers);
      setRows(Array.isArray(r?.reports) ? r.reports : []);
    } catch (e) { setErr(e?.message || String(e)); }
  }
  useEffect(() => { load(); }, [showAll]); // eslint-disable-line react-hooks/exhaustive-deps

  async function act(id, fn) {
    setBusyId(id); setErr('');
    try { await fn(); await load(); }
    catch (e) {
      let m = e?.message || 'Failed';
      try { m = JSON.parse(m).error || m; } catch (_) {}
      setErr(m);
    } finally { setBusyId(null); }
  }
  const acknowledge = (id) => act(id, () => api('POST', `/api/admin/shift-handoff/${id}/acknowledge`, {}, headers));
  const followUp = (id) => {
    const note = (notes[id] || '').trim();
    if (!note) return;
    return act(id, async () => {
      await api('POST', `/api/admin/shift-handoff/${id}/close`, { reporter_note: note }, headers);
      setNotes((c) => ({ ...c, [id]: '' }));
    });
  };

  if (!rows) return <div className="card text-sm text-gray-500">Loading…</div>;

  return (
    <div className="space-y-3">
      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
      <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div className="text-base font-semibold">Shift handoff reports</div>
          <div style={{ fontSize: 12, color: '#757575' }}>
            What one shift left for the next. Acknowledge quickly — the person who reported it can see it.
          </div>
        </div>
        <button onClick={() => setShowAll((v) => !v)} className="btn-secondary" style={{ fontSize: 12 }}>
          {showAll ? 'Show open only' : 'Show all'}
        </button>
      </div>

      {err && <div className="card" style={{ borderLeft: '4px solid #c62828', fontSize: 13, color: '#a8323a' }}>{err}</div>}
      {rows.length === 0 && <div className="card text-sm text-gray-500">Nothing here. That’s the good outcome.</div>}

      {rows.map((r) => {
        const ctx = r.context || {};
        const v = ctx.volume || {};
        return (
          <div key={r.id} className="card"
               style={{ borderLeft: `4px solid ${r.severity === 'high' ? '#c62828' : '#b48a47'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 6 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                {r.location_name} · {r.shift_date} {r.handoff_shift_kind}
              </div>
              <div style={{ fontSize: 12, color: '#757575' }}>reported by {r.reporter_name || 'unknown'}</div>
            </div>

            {/* Context first, deliberately. */}
            <div style={{ background: '#f1f7f2', borderRadius: 6, padding: '7px 10px', margin: '8px 0', fontSize: 12.5, color: '#37474f' }}>
              <b>{ctx.people_on ?? '—'} on</b>
              {ctx.people_scheduled != null && ctx.people_scheduled !== ctx.people_on ? ` (${ctx.people_scheduled} scheduled)` : ''}
              {ctx.labor_hours ? ` · ${ctx.labor_hours}h` : ''}
              {v.transactions != null ? ` · ${v.transactions} transactions ${v.window || ''}` : ''}
              {v.delta_pct != null && (
                <span style={{ color: v.delta_pct >= 0 ? '#a8323a' : '#2e5d42', fontWeight: 700 }}>
                  {' '}({v.delta_pct >= 0 ? '+' : ''}{v.delta_pct}% vs normal)
                </span>
              )}
              {(ctx.worked || []).length > 0 && (
                <div style={{ marginTop: 3, color: '#546e7a' }}>
                  On that shift: {ctx.worked.map((w) => `${w.name} (${w.hours}h)`).join(', ')}
                </div>
              )}
              {(ctx.notes || []).length > 0 && (
                <div style={{ marginTop: 3, color: '#8a5a1f' }}>{ctx.notes.join(' · ')}</div>
              )}
            </div>

            <div style={{ fontSize: 13 }}>
              {(r.items || []).map((i, idx) => (
                <div key={idx} style={{ marginBottom: 2 }}>
                  • <b>{i.label}</b>{i.note ? ` — ${i.note}` : ''}
                  {i.not_on_list && <span style={{ background: '#fff3e0', color: '#8a5a1f', borderRadius: 4, padding: '1px 6px', fontSize: 11, marginLeft: 6 }}>not on any checklist</span>}
                </div>
              ))}
            </div>

            {/* CJ 2026-09-30: the photos live here too, not only in the email. */}
            {(r.photos || []).length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                {r.photos.map((p) => (
                  <button key={p.id} onClick={() => setLightboxSrc(p.url)} title={p.item_label || 'Photo'}
                    style={{ padding: 0, border: '1px solid #e0e0e0', borderRadius: 6, background: '#f4f4f3',
                             cursor: 'zoom-in', overflow: 'hidden', width: 96, height: 96 }}>
                    <img src={p.url} alt={p.item_label || 'Handoff photo'} loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </button>
                ))}
              </div>
            )}

            <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {r.acknowledged_at
                ? <span style={{ fontSize: 12, color: '#2e5d42', fontWeight: 600 }}>
                    ✓ Acknowledged{r.acknowledged_by_name ? ` by ${r.acknowledged_by_name}` : ''}
                  </span>
                : <button onClick={() => acknowledge(r.id)} disabled={busyId === r.id}
                    style={{ background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 7,
                             padding: '6px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Acknowledge
                  </button>}
            </div>

            {r.reporter_note
              ? <div style={{ fontSize: 12.5, background: '#f7f7f7', borderRadius: 6, padding: '6px 10px', marginTop: 8 }}>
                  <b>Told the reporter:</b> {r.reporter_note}
                </div>
              : (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>
                    What did you do about it? (the reporter sees this)
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <input value={notes[r.id] || ''} onChange={(e) => setNotes((c) => ({ ...c, [r.id]: e.target.value }))}
                      placeholder="e.g. Talked to the closing team, ovens added to the list"
                      style={{ flex: 1, minWidth: 200, border: '1px solid #d0d4d9', borderRadius: 8, padding: '7px 10px', fontSize: 13, fontFamily: 'inherit' }} />
                    <button onClick={() => followUp(r.id)} disabled={busyId === r.id || !(notes[r.id] || '').trim()}
                      className="btn-secondary" style={{ fontSize: 12 }}>Send &amp; close</button>
                  </div>
                </div>
              )}
          </div>
        );
      })}
    </div>
  );
}
